import React, { useState, useEffect } from 'react';
import { getAffirmations, createAffirmation, deleteAffirmation } from './db';
import { calculateSPRSScore } from './data/controlWeights';

export default function Affirmation({ session, answers, level, profile }) {
  const [affirmations, setAffirmations] = useState([]);
  const [showModal, setShowModal]       = useState(false);
  const [loading, setLoading]           = useState(false);
  const [form, setForm]                 = useState({
    affirmed_by:    '',
    affirmer_title: '',
    title:          'Annual CMMC Affirmation',
    notes:          '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (session) loadAffirmations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (profile) {
      setForm(prev => ({
        ...prev,
        affirmed_by:    profile.contact_name || prev.affirmed_by,
        affirmer_title: profile.contact_title || prev.affirmer_title,
        title:          profile.company_name
          ? `${profile.company_name} Annual CMMC Affirmation`
          : prev.title,
      }));
    }
  }, [profile]);

  async function loadAffirmations() {
    if (!session?.user?.id) return;
    try {
      const data = await getAffirmations(session.user.id);
      setAffirmations(data || []);
    } catch (err) {
      console.error('loadAffirmations error:', err);
    }
  }

  async function handleAffirm() {
    if (!form.affirmed_by.trim()) return;
    setError('');
    setLoading(true);
    try {
      const sprsScore  = calculateSPRSScore(answers || {});
      const affirmedBy = form.affirmer_title.trim()
        ? `${form.affirmed_by.trim()}, ${form.affirmer_title.trim()}`
        : form.affirmed_by.trim();
      const result = await createAffirmation(session.user.id, {
        affirmed_by: affirmedBy,
        title:       form.title,
        notes:       form.notes,
        sprs_score:  sprsScore,
        level,
        affirmed_at: new Date().toISOString(),
      });
      if (result) {
        await loadAffirmations();
        setShowModal(false);
        setForm(prev => ({ ...prev, notes: '', affirmer_title: '' }));
      } else {
        setError('Failed to save affirmation. Please try again.');
      }
    } catch (err) {
      console.error('handleAffirm error:', err);
      setError('An error occurred. Please try again.');
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    try {
      await deleteAffirmation(id);
      setAffirmations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('handleDelete error:', err);
    }
  }

  const latest        = affirmations[0] || null;
  const lastDate      = latest ? new Date(latest.affirmed_at) : null;
  const nextDueDate   = lastDate
    ? new Date(lastDate.getTime() + 365 * 24 * 60 * 60 * 1000)
    : null;
  const daysUntilDue  = nextDueDate
    ? Math.ceil((nextDueDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isOverdue     = daysUntilDue !== null && daysUntilDue < 0;
  const isDueSoon     = daysUntilDue !== null && daysUntilDue <= 30 && daysUntilDue >= 0;
  const neverAffirmed = !latest;
  const currentSPRS   = calculateSPRSScore(answers);

  function getStatusColor() {
    if (neverAffirmed || isOverdue) return 'var(--red)';
    if (isDueSoon) return 'var(--amber)';
    return 'var(--green)';
  }

  function getStatusBg() {
    if (neverAffirmed || isOverdue) return 'var(--red-bg)';
    if (isDueSoon) return 'var(--amber-bg)';
    return 'var(--green-bg)';
  }

  function getStatusLabel() {
    if (neverAffirmed) return 'Never Affirmed';
    if (isOverdue) return 'Overdue';
    if (isDueSoon) return 'Due Soon';
    return 'Current';
  }

  function getStatusMessage() {
    if (neverAffirmed) {
      return 'You have not submitted an annual affirmation yet. DoD requires annual affirmation of your CMMC compliance status.';
    }
    if (isOverdue) {
      return `Your affirmation is ${Math.abs(daysUntilDue)} days overdue. Submit your annual affirmation immediately to maintain compliance eligibility.`;
    }
    if (isDueSoon) {
      return `Your annual affirmation is due in ${daysUntilDue} days. Schedule time to review your compliance posture and submit your affirmation.`;
    }
    return `Your affirmation is current. Next affirmation due in ${daysUntilDue} days on ${nextDueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`;
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)',
    border: '1px solid var(--border2)', borderRadius: 6,
    padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none',
  };

  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
            Annual Affirmation Tracker
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            DoD requires annual affirmation of your CMMC compliance status
          </div>
        </div>
        <button
          onClick={() => { setError(''); setShowModal(true); }}
          style={{
            fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}
        >
          + Submit Affirmation
        </button>
      </div>

      {/* Status Card */}
      <div style={{
        background: 'var(--bg2)',
        border: `1px solid ${getStatusColor()}`,
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 16,
        }}>
          <div>
            <div style={{
              fontSize: 11, color: 'var(--text3)', marginBottom: 4,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Affirmation Status
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: getStatusColor() }}>
              {getStatusLabel()}
            </div>
          </div>
          {daysUntilDue !== null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: 36, fontWeight: 800,
                color: getStatusColor(), lineHeight: 1,
              }}>
                {Math.abs(daysUntilDue)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                days {isOverdue ? 'overdue' : 'remaining'}
              </div>
            </div>
          )}
        </div>

        <div style={{
          fontSize: 12, lineHeight: 1.6, marginBottom: 16,
          padding: '10px 14px', background: getStatusBg(),
          borderRadius: 8, border: `1px solid ${getStatusColor()}`,
          color: getStatusColor(),
        }}>
          {getStatusMessage()}
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
        }}>
          {[
            {
              label: 'Last Affirmed',
              value: lastDate
                ? lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'Never',
              color: lastDate ? 'var(--text)' : 'var(--red)',
            },
            {
              label: 'Next Due',
              value: nextDueDate
                ? nextDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'ASAP',
              color: isOverdue ? 'var(--red)' : isDueSoon ? 'var(--amber)' : 'var(--text)',
            },
            {
              label: 'Current SPRS Score',
              value: currentSPRS,
              color: currentSPRS >= 88 ? 'var(--green)' : currentSPRS >= 0 ? 'var(--amber)' : 'var(--red)',
            },
          ].map(item => (
            <div key={item.label} style={{
              background: 'var(--surface)', borderRadius: 8,
              padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: item.color, marginBottom: 4 }}>
                {item.value}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* DoD Requirements */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 14, paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
        }}>
          DoD Affirmation Requirements
        </div>
        {[
          {
            title: 'Annual Affirmation Required',
            desc:  'All defense contractors with CMMC Level 2 certification must submit an annual affirmation confirming their compliance posture has not degraded.',
          },
          {
            title: 'Senior Official Must Sign',
            desc:  'The affirmation must be submitted by a senior company official — typically a CISO, CTO, or company officer with authority to represent the organization.',
          },
          {
            title: 'SPRS Score Must Be Current',
            desc:  'Your SPRS score in the DoD system must reflect your current compliance posture. Update it before affirming if your score has changed significantly.',
          },
          {
            title: '180-Day POA&M Window',
            desc:  'Any open POA&M items must be closed within 180 days of your conditional certification. Annual affirmations confirm you are on track with remediation.',
          },
        ].map((item, i) => (
          <div key={item.title} style={{
            display: 'flex', gap: 14,
            marginBottom: i < 3 ? 14 : 0,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: 'var(--accent)',
            }}>
              {i + 1}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>
                {item.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                {item.desc}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Affirmation History */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 14, paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          Affirmation History
          <span style={{
            fontSize: 11, color: 'var(--text3)', fontWeight: 400,
          }}>
            {affirmations.length} records
          </span>
        </div>

        {affirmations.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 0',
            fontSize: 12, color: 'var(--text3)',
          }}>
            No affirmations submitted yet. Click Submit Affirmation to record your first.
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 140px 120px 90px 40px',
              gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '0.07em',
            }}>
              <div>Title</div>
              <div>Affirmed By</div>
              <div>Date</div>
              <div>SPRS Score</div>
              <div></div>
            </div>

            {affirmations.map((a, i) => (
              <div key={a.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 140px 120px 90px 40px',
                gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)',
                alignItems: 'center', fontSize: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {a.title}
                    {i === 0 && (
                      <span style={{
                        fontSize: 9, fontWeight: 600,
                        padding: '2px 6px', borderRadius: 8,
                        background: 'var(--green-bg)', color: 'var(--green)',
                      }}>
                        Latest
                      </span>
                    )}
                  </div>
                  {a.notes && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                      {a.notes}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                  {a.affirmed_by}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                  {new Date(a.affirmed_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 700,
                  color: a.sprs_score >= 88 ? 'var(--green)' :
                         a.sprs_score >= 0  ? 'var(--amber)' : 'var(--red)',
                }}>
                  {a.sprs_score}
                </div>
                <div
                  onClick={() => handleDelete(a.id)}
                  style={{
                    fontSize: 14, color: 'var(--text3)',
                    cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  ×
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Affirmation Modal */}
      {showModal && (
        <div
          onClick={e => e.target === e.currentTarget && (setError(''), setShowModal(false))}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 12, padding: 28, width: 480, maxWidth: '90vw',
          }}>
            <div style={{
              fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6,
            }}>
              Submit Annual Affirmation
            </div>
            <div style={{
              fontSize: 12, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.5,
            }}>
              By submitting this affirmation you confirm that your organization
              remains compliant with CMMC Level {level} requirements and your
              compliance posture has not materially degraded since your last assessment.
            </div>

            {/* SPRS Score Display */}
            <div style={{
              background: 'var(--surface)', borderRadius: 8,
              padding: '12px 16px', marginBottom: 20,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                Current SPRS Score being affirmed
              </div>
              <div style={{
                fontSize: 22, fontWeight: 700,
                color: currentSPRS >= 88 ? 'var(--green)' :
                       currentSPRS >= 0  ? 'var(--amber)' : 'var(--red)',
              }}>
                {currentSPRS}
              </div>
            </div>

            {/* Form Fields */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Affirmation Title</label>
              <input
                type="text"
                placeholder="e.g. Annual CMMC Level 2 Affirmation"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Senior Official Name *</label>
                <input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={form.affirmed_by}
                  onChange={e => setForm(prev => ({ ...prev, affirmed_by: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
              <div>
                <label style={labelStyle}>Title / Role</label>
                <input
                  type="text"
                  placeholder="e.g. CISO, IT Director"
                  value={form.affirmer_title}
                  onChange={e => setForm(prev => ({ ...prev, affirmer_title: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Notes (Optional)</label>
              <textarea
                placeholder="Any notes about this affirmation period..."
                value={form.notes}
                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {/* Legal Notice */}
            <div style={{
              background: 'var(--amber-bg)', border: '1px solid var(--amber)',
              borderRadius: 8, padding: '10px 14px', marginBottom: error ? 12 : 20,
              fontSize: 11, color: 'var(--amber)', lineHeight: 1.5,
            }}>
              By submitting this affirmation you attest that the information is accurate
              to the best of your knowledge. False statements may subject your organization
              to penalties under the False Claims Act.
            </div>

            {error && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid var(--red)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 12, color: 'var(--red)',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setError(''); setShowModal(false); }}
                style={{
                  flex: 1, padding: '10px', borderRadius: 6,
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  border: '1px solid var(--border2)',
                  background: 'var(--surface)', color: 'var(--text2)',
                  fontFamily: 'var(--font)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAffirm}
                disabled={loading || !form.affirmed_by.trim()}
                style={{
                  flex: 2, padding: '10px', borderRadius: 6,
                  fontSize: 13, fontWeight: 600,
                  cursor: loading || !form.affirmed_by.trim() ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: '#fff',
                  fontFamily: 'var(--font)',
                  opacity: loading || !form.affirmed_by.trim() ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {loading ? 'Submitting...' : 'Submit Affirmation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}